// AI Sentry — Autonomy Trace
// Replayable incident command surface for cyber-physical environments
// Full-screen, dark-locked, standalone (no MainLayout)

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactFlow, {
  Background,
  useNodesState,
  useEdgesState,
  Handle,
  Position,
  BackgroundVariant,
} from 'reactflow';
import { motion, AnimatePresence } from 'framer-motion';
import 'reactflow/dist/style.css';
import { CAMPAIGN, INITIAL_NODES, INITIAL_EDGES } from '../data/scenarios/substationCampaign';

// ---------------------------------------------------------------------------
// Color system
// ---------------------------------------------------------------------------
const PROTOCOL_COLORS = {
  dnp3: '#06b6d4',
  iec61850_mms: '#a855f7',
  iec61850_goose: '#f97316',
  ssh: '#ef4444',
  trust: '#374151',
  monitoring: '#6d28d9',
};

const STATE_STYLES = {
  normal:     { border: 'border-blue-500/30',   bg: 'bg-blue-500/5',   text: 'text-blue-300',   glow: '',                       ring: '#3b82f6' },
  suspicious: { border: 'border-amber-500',      bg: 'bg-amber-500/10', text: 'text-amber-300',  glow: 'shadow-amber-500/40',    ring: '#f59e0b' },
  critical:   { border: 'border-red-500',        bg: 'bg-red-500/10',   text: 'text-red-300',    glow: 'shadow-red-500/50',      ring: '#ef4444' },
  isolated:   { border: 'border-gray-600',       bg: 'bg-gray-800/30',  text: 'text-gray-500',   glow: '',                       ring: '#4b5563' },
  ai:         { border: 'border-violet-500/50',  bg: 'bg-violet-500/5', text: 'text-violet-300', glow: 'shadow-violet-500/20',   ring: '#8b5cf6' },
};

const SEV_COLORS = {
  Critical:     'text-red-400 bg-red-500/10 border-red-500/40',
  Warning:      'text-amber-400 bg-amber-500/10 border-amber-500/40',
  Informational:'text-blue-400 bg-blue-500/10 border-blue-500/40',
};

// eslint-disable-next-line no-unused-vars
const ACTION_COLORS = {
  blocked:        'text-red-400',
  alerted:        'text-amber-400',
  action_required:'text-rose-400 font-semibold animate-pulse',
  logged:         'text-blue-400',
};

// ---------------------------------------------------------------------------
// Topology helpers
// ---------------------------------------------------------------------------
function applyDeltas(initialNodes, initialEdges, events, upToIdx, extraDelta) {
  let nodes = initialNodes.map(n => ({ ...n, data: { ...n.data } }));
  let edges = initialEdges.map(e => ({ ...e, data: { ...e.data } }));

  for (let i = 0; i <= upToIdx && i < events.length; i++) {
    const delta = events[i].topology_delta;
    if (!delta) continue;

    if (delta.nodes_add) nodes = [...nodes, ...delta.nodes_add.map(n => ({ ...n, data: { ...n.data } }))];
    if (delta.nodes_update) {
      nodes = nodes.map(n =>
        delta.nodes_update[n.id]
          ? { ...n, data: { ...n.data, ...delta.nodes_update[n.id] } }
          : n
      );
    }
    if (delta.edges_add) edges = [...edges, ...delta.edges_add.map(e => ({ ...e, data: { ...e.data } }))];
    if (delta.edges_update) {
      edges = edges.map(e =>
        delta.edges_update[e.id]
          ? { ...e, data: { ...e.data, ...delta.edges_update[e.id]?.data }, ...Object.fromEntries(Object.entries(delta.edges_update[e.id]).filter(([k]) => k !== 'data')) }
          : e
      );
    }
    if (delta.edges_remove) edges = edges.filter(e => !delta.edges_remove.includes(e.id));
  }

  if (extraDelta) {
    if (extraDelta.nodes_update) {
      nodes = nodes.map(n =>
        extraDelta.nodes_update[n.id]
          ? { ...n, data: { ...n.data, ...extraDelta.nodes_update[n.id] } }
          : n
      );
    }
    if (extraDelta.edges_update) {
      edges = edges.map(e =>
        extraDelta.edges_update[e.id]
          ? { ...e, data: { ...e.data, ...extraDelta.edges_update[e.id]?.data } }
          : e
      );
    }
  }

  return { nodes, edges };
}

function edgeStyle(data) {
  const color = PROTOCOL_COLORS[data?.protocol] || '#4b5563';
  const s = data?.state || 'normal';
  const base = { stroke: color, strokeWidth: s === 'critical' ? 2.5 : 1.5 };
  if (s === 'blocked')    return { ...base, strokeDasharray: '4 4', opacity: 0.3 };
  if (s === 'suspicious') return { ...base, strokeDasharray: '6 3' };
  if (s === 'critical')   return { ...base };
  if (data?.protocol === 'monitoring') return { ...base, strokeDasharray: '3 3', opacity: 0.5 };
  if (data?.protocol === 'trust')     return { ...base, opacity: 0.35 };
  return base;
}

// ---------------------------------------------------------------------------
// Custom DeviceNode
// ---------------------------------------------------------------------------
const DeviceNode = ({ data, selected }) => {
  const s = STATE_STYLES[data.state] || STATE_STYLES.normal;
  const isPulsing = data.state === 'suspicious' || data.state === 'critical';
  const isIsolated = data.state === 'isolated';

  return (
    <div
      className={`
        relative rounded-xl border px-3 py-2.5 min-w-[130px] cursor-pointer
        transition-all duration-300
        ${s.border} ${s.bg}
        ${isPulsing ? `shadow-lg ${s.glow}` : ''}
        ${isIsolated ? 'opacity-50 grayscale' : ''}
        ${selected ? 'ring-2 ring-white/30' : ''}
      `}
      style={{ background: 'rgba(10,13,20,0.85)', backdropFilter: 'blur(8px)' }}
    >
      {isPulsing && (
        <span
          className="absolute inset-0 rounded-xl animate-ping opacity-20"
          style={{ background: s.ring, animationDuration: '2s' }}
        />
      )}

      <Handle type="target" position={Position.Top} style={{ opacity: 0, width: 8, height: 8 }} />
      <Handle type="source" position={Position.Bottom} style={{ opacity: 0, width: 8, height: 8 }} />
      <Handle type="target" position={Position.Left} style={{ opacity: 0, width: 8, height: 8 }} />
      <Handle type="source" position={Position.Right} style={{ opacity: 0, width: 8, height: 8 }} />

      <div className="flex items-center gap-2 mb-1">
        <span className={`text-base leading-none ${s.text}`}>{data.icon}</span>
        <span className={`text-[11px] font-bold tracking-wide ${s.text}`}>{data.label}</span>
        {data.alertCount > 0 && (
          <span className={`ml-auto text-[9px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center
            ${data.state === 'critical' ? 'bg-red-500/30 text-red-300' :
              data.state === 'suspicious' ? 'bg-amber-500/30 text-amber-300' :
              'bg-gray-700 text-gray-300'}`}>
            {data.alertCount}
          </span>
        )}
      </div>

      <div className="text-[9px] text-gray-500 font-mono leading-tight">{data.ip}</div>
      {data.sublabel && <div className="text-[9px] text-gray-600 leading-tight mt-0.5">{data.sublabel}</div>}
      {data.role && (
        <div className={`mt-1.5 text-[8px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded
          ${data.state === 'isolated' ? 'bg-gray-700 text-gray-500' : 'bg-white/5 text-gray-400'}`}>
          {data.role}
        </div>
      )}
    </div>
  );
};

const nodeTypes = { device: DeviceNode };

// ---------------------------------------------------------------------------
// Topology Canvas
// ---------------------------------------------------------------------------
const TopologyCanvas = ({ rfNodes, rfEdges, onNodeClick }) => {
  const [, , onNodesChange] = useNodesState(rfNodes);
  const [, , onEdgesChange] = useEdgesState(rfEdges);

  // Sync external state into React Flow
  const syncedNodes = rfNodes;
  const syncedEdges = rfEdges.map(e => ({
    ...e,
    style: edgeStyle(e.data),
    animated: e.data?.state === 'suspicious' || e.data?.state === 'critical',
    labelStyle: { fill: '#9ca3af', fontSize: 9, fontFamily: 'monospace' },
    labelBgStyle: { fill: 'rgba(10,13,20,0.7)', fillOpacity: 1 },
    label: e.data?.label || '',
    type: 'smoothstep',
  }));

  return (
    <ReactFlow
      nodes={syncedNodes}
      edges={syncedEdges}
      nodeTypes={nodeTypes}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      onNodeClick={(_, node) => onNodeClick(node)}
      fitView
      fitViewOptions={{ padding: 0.15 }}
      minZoom={0.4}
      maxZoom={2}
      proOptions={{ hideAttribution: true }}
      className="bg-transparent"
    >
      <Background variant={BackgroundVariant.Dots} gap={24} size={1} color="rgba(255,255,255,0.04)" />
    </ReactFlow>
  );
};

// ---------------------------------------------------------------------------
// Decision Card
// ---------------------------------------------------------------------------
const DecisionCard = ({ event, index, isLatest, onFocus }) => {
  const [expanded, setExpanded] = useState(isLatest);
  const sev = event.severity;
  const isActionRequired = event.action_status === 'action_required';

  useEffect(() => { if (isLatest) setExpanded(true); }, [isLatest]);

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3, delay: 0.05 * index }}
      className={`rounded-xl border mb-2 overflow-hidden cursor-pointer transition-all duration-200
        ${isActionRequired
          ? 'border-rose-500/60 bg-rose-500/5'
          : sev === 'Critical'
          ? 'border-red-500/40 bg-red-500/5'
          : sev === 'Warning'
          ? 'border-amber-500/30 bg-amber-500/5'
          : 'border-blue-500/20 bg-blue-500/5'}
        ${isLatest ? 'ring-1 ring-white/10' : 'opacity-80'}`}
      onClick={() => { setExpanded(e => !e); onFocus(event); }}
    >
      {/* Card header */}
      <div className="px-3 py-2 flex items-start gap-2">
        <div className={`mt-0.5 w-1.5 h-1.5 rounded-full flex-shrink-0 mt-1.5
          ${isActionRequired ? 'bg-rose-400 animate-pulse' :
            sev === 'Critical' ? 'bg-red-400' :
            sev === 'Warning' ? 'bg-amber-400' : 'bg-blue-400'}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-1">
            <span className="text-[11px] font-semibold text-gray-100 leading-snug">{event.title}</span>
            {isActionRequired && (
              <span className="text-[8px] font-bold text-rose-400 bg-rose-500/20 px-1.5 py-0.5 rounded uppercase tracking-wide flex-shrink-0">
                Action required
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-[9px] font-mono text-gray-500">{event.timestamp}</span>
            <span className={`text-[9px] font-bold px-1 py-px rounded border ${SEV_COLORS[sev] || SEV_COLORS.Informational}`}>
              {sev}
            </span>
            {event.policy_ids?.slice(0, 3).map(p => (
              <span key={p} className="text-[8px] font-mono text-violet-400 bg-violet-500/10 px-1 py-px rounded">
                {p}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Expanded body */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-3 pb-3 space-y-2 border-t border-white/5 pt-2">
              <CardRow label="Observed" value={event.card.observed} color="text-gray-300" />
              <CardRow label="Inferred" value={event.card.inferred} color="text-violet-300" />
              <CardRow label="Why it matters" value={event.card.why_it_matters} color="text-amber-200/80" />
              <CardRow label="Recommendation" value={event.card.recommendation} color="text-cyan-300" />
              <div className="flex items-center justify-between pt-1">
                <div className="flex items-center gap-1">
                  <span className="text-[9px] text-gray-500">Confidence</span>
                  <span className="text-[10px] font-bold text-cyan-400">{event.card.confidence}</span>
                </div>
                {event.source_ip && (
                  <span className="text-[9px] font-mono text-gray-500">{event.source_ip} → {event.dest_ip || 'broadcast'}</span>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const CardRow = ({ label, value, color }) => (
  <div>
    <span className="text-[8px] font-bold uppercase tracking-wider text-gray-600">{label}</span>
    <p className={`text-[10px] leading-snug mt-0.5 ${color}`}>{value}</p>
  </div>
);

// ---------------------------------------------------------------------------
// Action Preview Modal
// ---------------------------------------------------------------------------
const ActionPreview = ({ onApprove, onCancel }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
    onClick={onCancel}
  >
    <motion.div
      initial={{ scale: 0.95, y: 10 }}
      animate={{ scale: 1, y: 0 }}
      exit={{ scale: 0.95 }}
      onClick={e => e.stopPropagation()}
      className="w-[520px] rounded-2xl border border-red-500/30 bg-[#0e1118] shadow-2xl overflow-hidden"
    >
      <div className="px-6 py-4 border-b border-white/5 flex items-center justify-between">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-red-400 mb-0.5">Containment Action</div>
          <div className="text-lg font-bold text-white">Isolate 192.168.1.100</div>
        </div>
        <span className="text-[10px] font-bold bg-red-500/15 text-red-400 border border-red-500/30 px-2 py-1 rounded-lg uppercase tracking-wide">
          Approval required
        </span>
      </div>

      <div className="px-6 py-4 space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <PreviewStat label="Impacted assets" value="3" sub="RTU · Relay · IED edges severed" />
          <PreviewStat label="Expected risk reduction" value="94%" sub="All rogue traffic stopped" color="text-green-400" />
          <PreviewStat label="Operational side effects" value="None" sub="No authorized traffic impacted" color="text-green-400" />
          <PreviewStat label="Blast radius" value="Rogue only" sub="192.168.1.100 fully isolated" />
        </div>

        <div>
          <div className="text-[9px] font-bold uppercase tracking-wider text-gray-500 mb-1.5">Policies authorizing action</div>
          <div className="flex flex-wrap gap-1.5">
            {['P34', 'P36', 'P40', 'P46'].map(p => (
              <span key={p} className="text-[10px] font-mono text-violet-300 bg-violet-500/10 border border-violet-500/20 px-2 py-0.5 rounded">
                {p}
              </span>
            ))}
          </div>
        </div>

        <div className="rounded-xl bg-white/3 border border-white/5 p-3">
          <div className="text-[9px] font-bold uppercase tracking-wider text-gray-500 mb-2">What will change</div>
          <div className="space-y-1.5">
            {[
              { label: 'DNP3 traffic from 192.168.1.100', before: 'Active / Suspicious', after: 'Blocked', afterColor: 'text-red-400' },
              { label: 'IEC 61850 MMS from 192.168.1.100', before: 'Blocked by P40', after: 'Isolated (host level)', afterColor: 'text-red-400' },
              { label: 'SSH from 192.168.1.100', before: 'Blocked by P46', after: 'Isolated (host level)', afterColor: 'text-red-400' },
              { label: 'RTU / Relay / IED state', before: 'Critical alerts', after: 'Recovering', afterColor: 'text-green-400' },
            ].map(row => (
              <div key={row.label} className="flex items-center gap-2 text-[10px]">
                <span className="text-gray-400 flex-1">{row.label}</span>
                <span className="text-gray-600 font-mono">{row.before}</span>
                <span className="text-gray-600">→</span>
                <span className={`font-semibold font-mono ${row.afterColor}`}>{row.after}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="px-6 py-4 border-t border-white/5 flex gap-3">
        <button
          onClick={onApprove}
          className="flex-1 py-2.5 rounded-xl bg-red-500 hover:bg-red-600 text-white font-bold text-sm transition-colors"
        >
          Approve Isolation
        </button>
        <button
          onClick={onCancel}
          className="px-4 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 text-gray-300 font-medium text-sm transition-colors"
        >
          Cancel
        </button>
      </div>
    </motion.div>
  </motion.div>
);

const PreviewStat = ({ label, value, sub, color = 'text-white' }) => (
  <div className="rounded-lg bg-white/3 border border-white/5 p-3">
    <div className="text-[9px] text-gray-500 uppercase tracking-wide mb-1">{label}</div>
    <div className={`text-xl font-bold ${color}`}>{value}</div>
    {sub && <div className="text-[9px] text-gray-500 mt-0.5">{sub}</div>}
  </div>
);

// ---------------------------------------------------------------------------
// After-Action Panel
// ---------------------------------------------------------------------------
const AfterActionPanel = ({ data, onClose }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
    onClick={onClose}
  >
    <motion.div
      initial={{ scale: 0.95, y: 10 }}
      animate={{ scale: 1, y: 0 }}
      exit={{ scale: 0.95 }}
      onClick={e => e.stopPropagation()}
      className="w-[640px] max-h-[85vh] overflow-y-auto rounded-2xl border border-white/10 bg-[#0a0d14] shadow-2xl"
    >
      <div className="sticky top-0 px-6 py-4 border-b border-white/5 bg-[#0a0d14] flex items-center justify-between">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-widest text-green-400 mb-0.5">After-Action Report</div>
          <div className="text-lg font-bold text-white">{CAMPAIGN.title}</div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[10px] font-bold bg-green-500/15 text-green-400 border border-green-500/30 px-2 py-1 rounded-lg uppercase tracking-wide">
            {data.status}
          </span>
          <button onClick={onClose} className="text-gray-500 hover:text-white transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      <div className="px-6 py-5 space-y-6">
        {/* Summary row */}
        <div className="grid grid-cols-3 gap-3">
          <AAStat label="Duration" value={data.duration} />
          <AAStat label="Start" value={data.start} mono />
          <AAStat label="End" value={data.end} mono />
        </div>

        {/* Devices */}
        <Section title="Devices Involved">
          <div className="space-y-2">
            {data.devices.map(d => (
              <div key={d.ip} className="flex items-center gap-3 text-[11px]">
                <span className="font-mono text-gray-500 w-28 flex-shrink-0">{d.ip}</span>
                <span className="text-gray-300 flex-1">{d.role}</span>
                <span className={`font-semibold ${d.outcome.includes('ISOLATED') ? 'text-red-400' : 'text-green-400'}`}>
                  {d.outcome}
                </span>
              </div>
            ))}
          </div>
        </Section>

        {/* Attack sequence */}
        <Section title="Attack Sequence">
          <div className="flex flex-wrap gap-2">
            {data.attackSequence.map((step, i) => (
              <div key={i} className="flex items-center gap-1.5">
                {i > 0 && <span className="text-gray-700">→</span>}
                <span className="text-[10px] bg-white/5 border border-white/8 text-gray-300 px-2 py-1 rounded-lg">{step}</span>
              </div>
            ))}
          </div>
        </Section>

        {/* Policies */}
        <Section title={`Policies Triggered — ${data.policiesTriggered.length}`}>
          <div className="flex flex-wrap gap-1.5">
            {data.policiesTriggered.map(p => (
              <span key={p} className="text-[10px] font-mono text-violet-300 bg-violet-500/10 border border-violet-500/20 px-2 py-0.5 rounded">
                {p}
              </span>
            ))}
          </div>
        </Section>

        {/* Enforcement */}
        <Section title="Enforcement Actions">
          <div className="space-y-2">
            <div>
              <div className="text-[9px] font-bold uppercase tracking-wide text-gray-600 mb-1">Auto-executed</div>
              {data.enforcementActions.auto.map((a, i) => (
                <div key={i} className="flex items-center gap-2 text-[10px] text-gray-300">
                  <span className="w-1 h-1 rounded-full bg-red-400 flex-shrink-0" />
                  {a}
                </div>
              ))}
            </div>
            <div>
              <div className="text-[9px] font-bold uppercase tracking-wide text-gray-600 mb-1 mt-2">Operator approved</div>
              {data.enforcementActions.manual.map((a, i) => (
                <div key={i} className="flex items-center gap-2 text-[10px] text-gray-300">
                  <span className="w-1 h-1 rounded-full bg-green-400 flex-shrink-0" />
                  {a}
                </div>
              ))}
            </div>
          </div>
        </Section>

        {/* Compliance */}
        <Section title="Compliance Mapping">
          {data.compliance.map((c, i) => (
            <div key={i} className="flex gap-3 text-[10px] mb-1.5">
              <span className="font-bold text-cyan-400 w-28 flex-shrink-0">{c.standard}</span>
              <span className="text-gray-400">{c.note}</span>
            </div>
          ))}
        </Section>

        {/* Unresolved */}
        {data.unresolved?.length > 0 && (
          <Section title="Unresolved">
            {data.unresolved.map((u, i) => (
              <div key={i} className="flex items-center gap-2 text-[10px] text-amber-300">
                <span className="text-amber-500">⚠</span> {u}
              </div>
            ))}
          </Section>
        )}

        {/* Export row */}
        <div className="flex gap-3 pt-2">
          <button className="flex-1 py-2 rounded-xl bg-white/5 hover:bg-white/8 text-gray-300 text-sm font-medium border border-white/8 transition-colors">
            Export JSON
          </button>
          <button className="flex-1 py-2 rounded-xl bg-white/5 hover:bg-white/8 text-gray-300 text-sm font-medium border border-white/8 transition-colors">
            Generate CIP Evidence Pack
          </button>
          <button onClick={onClose} className="px-5 py-2 rounded-xl bg-white/8 hover:bg-white/12 text-white text-sm font-medium transition-colors">
            Close
          </button>
        </div>
      </div>
    </motion.div>
  </motion.div>
);

const AAStat = ({ label, value, mono }) => (
  <div className="rounded-xl bg-white/3 border border-white/5 p-3">
    <div className="text-[9px] text-gray-500 uppercase tracking-wide mb-1">{label}</div>
    <div className={`text-base font-bold text-white ${mono ? 'font-mono' : ''}`}>{value}</div>
  </div>
);

const Section = ({ title, children }) => (
  <div>
    <div className="text-[9px] font-bold uppercase tracking-widest text-gray-600 mb-2">{title}</div>
    {children}
  </div>
);

// ---------------------------------------------------------------------------
// Replay speed labels
// ---------------------------------------------------------------------------
const SPEEDS = [1, 2, 4, 8];
const EVENT_INTERVAL_MS = 2800;

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export default function AutonomyTrace() {
  const navigate = useNavigate();
  const events = CAMPAIGN.events;
  const intervalRef = useRef(null);

  const [currentIdx, setCurrentIdx] = useState(-1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [actionApproved, setActionApproved] = useState(false);
  const [showActionPreview, setShowActionPreview] = useState(false);
  const [showAfterAction, setShowAfterAction] = useState(false);
  const [focusedEventId, setFocusedEventId] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);

  const isFinished = currentIdx >= events.length - 1;
  const visibleEvents = events.slice(0, Math.max(0, currentIdx + 1));
  const currentEvent = currentIdx >= 0 ? events[currentIdx] : null;
  const criticalCount = visibleEvents.filter(e => e.severity === 'Critical').length;
  const blockedCount = visibleEvents.filter(e => e.action_status === 'blocked').length;

  // Compute topology state
  const { nodes: rfNodes, edges: rfEdges } = useMemo(() => {
    return applyDeltas(
      INITIAL_NODES,
      INITIAL_EDGES,
      events,
      currentIdx,
      actionApproved ? CAMPAIGN.approvalDelta : null
    );
  }, [currentIdx, actionApproved]);

  // Replay controller
  useEffect(() => {
    if (!isPlaying) { clearInterval(intervalRef.current); return; }
    if (isFinished) { setIsPlaying(false); return; }

    intervalRef.current = setInterval(() => {
      setCurrentIdx(prev => {
        const next = prev + 1;
        if (next >= events.length - 1) setIsPlaying(false);
        return next;
      });
    }, EVENT_INTERVAL_MS / speed);

    return () => clearInterval(intervalRef.current);
  }, [isPlaying, speed, isFinished]);

  const handlePlayPause = () => {
    if (isFinished) { handleReset(); return; }
    setIsPlaying(p => !p);
  };

  const handleStepForward = () => {
    setIsPlaying(false);
    setCurrentIdx(prev => Math.min(prev + 1, events.length - 1));
  };

  const handleStepBack = () => {
    setIsPlaying(false);
    setCurrentIdx(prev => Math.max(prev - 1, -1));
  };

  const handleReset = () => {
    setIsPlaying(false);
    setCurrentIdx(-1);
    setActionApproved(false);
    setShowAfterAction(false);
    setFocusedEventId(null);
  };

  const handleApprove = () => {
    setActionApproved(true);
    setShowActionPreview(false);
    setTimeout(() => setShowAfterAction(true), 1200);
  };

  const handleNodeClick = useCallback((node) => {
    setSelectedNode(prev => prev?.id === node.id ? null : node);
  }, []);

  const sevDot = (sev) => sev === 'Critical' ? 'bg-red-500' : sev === 'Warning' ? 'bg-amber-500' : 'bg-blue-500';

  return (
    <div className="fixed inset-0 bg-[#080b14] flex flex-col overflow-hidden select-none"
         style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>

      {/* ---------------------------------------------------------------- */}
      {/* TOP COMMAND BAR                                                   */}
      {/* ---------------------------------------------------------------- */}
      <div className="h-12 flex-shrink-0 border-b border-white/5 bg-[#0a0d14]/90 backdrop-blur-md flex items-center px-4 gap-3">
        {/* Back */}
        <button
          onClick={() => navigate('/events')}
          className="flex items-center gap-1.5 text-gray-500 hover:text-gray-300 transition-colors text-sm mr-1"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        {/* Brand */}
        <div className="flex items-center gap-2 mr-2">
          <div className="w-6 h-6 rounded-lg bg-violet-600/80 flex items-center justify-center text-white text-xs font-bold">⚡</div>
          <span className="text-[11px] font-bold uppercase tracking-widest text-gray-300">Autonomy Trace</span>
        </div>

        <div className="w-px h-5 bg-white/8" />

        {/* Scenario label */}
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-gray-500">Scenario</span>
          <span className="text-[10px] font-semibold text-gray-200">{CAMPAIGN.title}</span>
          <span className="text-[9px] text-gray-600 font-mono">{CAMPAIGN.environment}</span>
        </div>

        <div className="flex-1" />

        {/* Status counters */}
        {criticalCount > 0 && (
          <div className="flex items-center gap-1.5 bg-red-500/10 border border-red-500/20 rounded-lg px-2 py-1">
            <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
            <span className="text-[10px] font-bold text-red-400">{criticalCount} Critical</span>
          </div>
        )}
        {blockedCount > 0 && (
          <div className="flex items-center gap-1.5 bg-orange-500/10 border border-orange-500/20 rounded-lg px-2 py-1">
            <span className="text-[10px] font-bold text-orange-400">{blockedCount} Blocked</span>
          </div>
        )}
        {currentEvent?.action_status === 'action_required' && !actionApproved && (
          <button
            onClick={() => setShowActionPreview(true)}
            className="flex items-center gap-1.5 bg-rose-500/15 border border-rose-500/40 hover:border-rose-400 rounded-lg px-3 py-1.5 transition-all"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse" />
            <span className="text-[10px] font-bold text-rose-300">Containment ready — approve</span>
          </button>
        )}
        {actionApproved && (
          <div className="flex items-center gap-1.5 bg-green-500/10 border border-green-500/20 rounded-lg px-2 py-1">
            <span className="text-[10px] font-bold text-green-400">✓ Contained</span>
          </div>
        )}

        <div className="w-px h-5 bg-white/8" />

        {/* Replay controls */}
        <div className="flex items-center gap-1">
          <CtrlBtn onClick={handleStepBack} title="Step back" disabled={currentIdx < 0}>
            <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M6 6h2v12H6zm3.5 6 8.5 6V6z" /></svg>
          </CtrlBtn>
          <CtrlBtn onClick={handlePlayPause} primary>
            {isPlaying
              ? <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" /></svg>
              : isFinished
              ? <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z" /></svg>
              : <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
            }
          </CtrlBtn>
          <CtrlBtn onClick={handleStepForward} title="Step forward" disabled={isFinished}>
            <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M6 18l8.5-6L6 6v12zM16 6v12h2V6h-2z" /></svg>
          </CtrlBtn>
          <CtrlBtn onClick={handleReset} title="Reset">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </CtrlBtn>
        </div>

        {/* Speed */}
        <div className="flex items-center gap-0.5 bg-white/5 rounded-lg p-0.5">
          {SPEEDS.map(s => (
            <button
              key={s}
              onClick={() => setSpeed(s)}
              className={`px-2 py-0.5 rounded-md text-[10px] font-bold transition-colors
                ${speed === s ? 'bg-violet-600 text-white' : 'text-gray-500 hover:text-gray-300'}`}
            >
              {s}×
            </button>
          ))}
        </div>

        {/* Event progress */}
        <div className="text-[10px] font-mono text-gray-600">
          {currentIdx + 1}/{events.length}
        </div>
      </div>

      {/* ---------------------------------------------------------------- */}
      {/* MAIN BODY                                                         */}
      {/* ---------------------------------------------------------------- */}
      <div className="flex flex-1 overflow-hidden">

        {/* LEFT RAIL — Incident timeline */}
        <div className="w-64 flex-shrink-0 border-r border-white/5 bg-[#090c15]/80 flex flex-col overflow-hidden">
          <div className="px-4 py-2.5 border-b border-white/5">
            <div className="text-[9px] font-bold uppercase tracking-widest text-gray-600">Incident Timeline</div>
            <div className="text-[10px] text-gray-500 mt-0.5">
              {visibleEvents.length} events · {CAMPAIGN.environment}
            </div>
          </div>

          {/* Scrubber */}
          <div className="px-4 py-2 border-b border-white/5">
            <input
              type="range"
              min={-1}
              max={events.length - 1}
              value={currentIdx}
              onChange={e => { setIsPlaying(false); setCurrentIdx(Number(e.target.value)); }}
              className="w-full h-1 accent-violet-500 cursor-pointer"
            />
            <div className="flex justify-between mt-1">
              <span className="text-[8px] font-mono text-gray-700">{events[0]?.timestamp}</span>
              <span className="text-[8px] font-mono text-gray-700">{events[events.length - 1]?.timestamp}</span>
            </div>
          </div>

          {/* Event list */}
          <div className="flex-1 overflow-y-auto px-3 py-2 space-y-1">
            {currentIdx === -1 && (
              <div className="text-center py-8">
                <div className="text-gray-600 text-xs mb-3">Press play to begin replay</div>
                <button
                  onClick={handlePlayPause}
                  className="px-4 py-2 rounded-xl bg-violet-600 hover:bg-violet-500 text-white text-xs font-semibold transition-colors"
                >
                  ▶ Start
                </button>
              </div>
            )}
            {visibleEvents.map((evt, i) => {
              const isCurrent = i === currentIdx;
              return (
                <div
                  key={evt.id}
                  onClick={() => { setIsPlaying(false); setCurrentIdx(i); setFocusedEventId(evt.id); }}
                  className={`rounded-lg px-2.5 py-2 cursor-pointer transition-all
                    ${isCurrent
                      ? 'bg-white/8 border border-white/10'
                      : 'hover:bg-white/4 border border-transparent'}`}
                >
                  <div className="flex items-center gap-2">
                    <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${sevDot(evt.severity)}`} />
                    <span className="text-[9px] font-mono text-gray-600">{evt.timestamp}</span>
                    {evt.action_status === 'blocked' && (
                      <span className="ml-auto text-[8px] text-red-400 font-bold">BLOCKED</span>
                    )}
                    {evt.action_status === 'action_required' && (
                      <span className="ml-auto text-[8px] text-rose-400 font-bold animate-pulse">ACTION</span>
                    )}
                  </div>
                  <div className={`text-[10px] font-medium mt-0.5 leading-snug
                    ${isCurrent ? 'text-white' : 'text-gray-400'}`}>
                    {evt.title}
                  </div>
                  <div className="flex items-center gap-1 mt-1">
                    {evt.protocol && (
                      <span className="text-[8px] font-mono px-1 rounded"
                            style={{ color: PROTOCOL_COLORS[evt.protocol] || '#6b7280',
                                     background: (PROTOCOL_COLORS[evt.protocol] || '#6b7280') + '15' }}>
                        {evt.protocol.toUpperCase()}
                      </span>
                    )}
                    {evt.policy_ids?.slice(0, 2).map(p => (
                      <span key={p} className="text-[8px] text-gray-600">{p}</span>
                    ))}
                  </div>
                </div>
              );
            })}
            {isFinished && !actionApproved && (
              <div className="mt-3 px-2">
                <button
                  onClick={() => setShowActionPreview(true)}
                  className="w-full py-2 rounded-xl bg-rose-500/20 hover:bg-rose-500/30 border border-rose-500/40 text-rose-300 text-[11px] font-semibold transition-colors"
                >
                  Review containment action
                </button>
              </div>
            )}
            {actionApproved && (
              <div className="mt-3 px-2">
                <button
                  onClick={() => setShowAfterAction(true)}
                  className="w-full py-2 rounded-xl bg-green-500/15 hover:bg-green-500/20 border border-green-500/30 text-green-300 text-[11px] font-semibold transition-colors"
                >
                  View after-action report
                </button>
              </div>
            )}
          </div>
        </div>

        {/* CENTER — Topology canvas */}
        <div className="flex-1 relative overflow-hidden">
          {/* Protocol legend */}
          <div className="absolute top-3 left-3 z-10 flex flex-col gap-1 bg-black/40 backdrop-blur-sm rounded-xl p-2.5 border border-white/5">
            {Object.entries({ DNP3: 'dnp3', MMS: 'iec61850_mms', GOOSE: 'iec61850_goose', SSH: 'ssh' }).map(([label, key]) => (
              <div key={key} className="flex items-center gap-2">
                <div className="w-5 h-0.5 rounded" style={{ background: PROTOCOL_COLORS[key] }} />
                <span className="text-[8px] font-mono" style={{ color: PROTOCOL_COLORS[key] }}>{label}</span>
              </div>
            ))}
          </div>

          {/* Node state legend */}
          <div className="absolute top-3 right-3 z-10 flex flex-col gap-1 bg-black/40 backdrop-blur-sm rounded-xl p-2.5 border border-white/5">
            {[['Normal', 'border-blue-500/30 text-blue-400'],
              ['Suspicious', 'border-amber-500 text-amber-400'],
              ['Critical', 'border-red-500 text-red-400'],
              ['Isolated', 'border-gray-600 text-gray-500 opacity-60']].map(([label, cls]) => (
              <div key={label} className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded border ${cls}`} />
                <span className={`text-[8px] ${cls.split(' ')[1]}`}>{label}</span>
              </div>
            ))}
          </div>

          {/* Selected node detail */}
          <AnimatePresence>
            {selectedNode && (
              <motion.div
                key="node-detail"
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 6 }}
                className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 bg-[#0e1118]/90 backdrop-blur-md border border-white/10 rounded-xl px-4 py-3 min-w-[260px]"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-bold text-white">{selectedNode.data.label}</span>
                  <button onClick={() => setSelectedNode(null)} className="text-gray-600 hover:text-gray-300 text-xs">✕</button>
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                  {[['IP', selectedNode.data.ip],
                    ['Role', selectedNode.data.role],
                    ['State', selectedNode.data.state],
                    ['Alerts', selectedNode.data.alertCount || 0],
                    ['Policy hits', selectedNode.data.policyHits || 0],
                    ['Device', selectedNode.data.sublabel || '—']].map(([k, v]) => (
                    <div key={k}>
                      <span className="text-[8px] uppercase tracking-wide text-gray-600">{k}</span>
                      <div className="text-[10px] font-medium text-gray-200 font-mono">{v}</div>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <TopologyCanvas
            rfNodes={rfNodes}
            rfEdges={rfEdges}
            onNodeClick={handleNodeClick}
          />
        </div>

        {/* RIGHT RAIL — Decision cards */}
        <div className="w-72 flex-shrink-0 border-l border-white/5 bg-[#090c15]/80 flex flex-col overflow-hidden">
          <div className="px-4 py-2.5 border-b border-white/5">
            <div className="text-[9px] font-bold uppercase tracking-widest text-gray-600">AI Decision Cards</div>
            <div className="text-[10px] text-gray-500 mt-0.5">
              {visibleEvents.length} of {events.length} events
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-3 py-3">
            {currentIdx === -1 && (
              <div className="text-center py-10 text-gray-600 text-xs">
                Cards appear as the replay advances
              </div>
            )}
            <AnimatePresence>
              {[...visibleEvents].reverse().map((evt, i) => (
                <DecisionCard
                  key={evt.id}
                  event={evt}
                  index={i}
                  isLatest={i === 0}
                  onFocus={(e) => setFocusedEventId(e.id)}
                />
              ))}
            </AnimatePresence>
          </div>

          {/* Policy summary footer */}
          {visibleEvents.length > 0 && (
            <div className="border-t border-white/5 px-3 py-3">
              <div className="text-[9px] font-bold uppercase tracking-wider text-gray-600 mb-2">Active policies</div>
              <div className="flex flex-wrap gap-1">
                {[...new Set(visibleEvents.flatMap(e => e.policy_ids || []))].map(p => (
                  <span key={p} className="text-[8px] font-mono text-violet-400 bg-violet-500/10 px-1.5 py-0.5 rounded border border-violet-500/20">
                    {p}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <AnimatePresence>
        {showActionPreview && (
          <ActionPreview
            onApprove={handleApprove}
            onCancel={() => setShowActionPreview(false)}
          />
        )}
        {showAfterAction && (
          <AfterActionPanel
            data={CAMPAIGN.afterAction}
            onClose={() => setShowAfterAction(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// Small reusable control button
const CtrlBtn = ({ onClick, children, primary, disabled, title }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    title={title}
    className={`w-7 h-7 rounded-lg flex items-center justify-center transition-all
      ${primary
        ? 'bg-violet-600 hover:bg-violet-500 text-white'
        : 'bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white'}
      ${disabled ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer'}`}
  >
    {children}
  </button>
);
